import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import SimpleBarReact from 'simplebar-react';
import { Table, Pagination } from 'react-bootstrap';
import classNames from 'classnames';
import moment from 'moment';
import AppContext from 'context/Context';

const getTotal = (data, key) =>
  data.reduce((acc, val) => acc + Number(val[key]), 0);

// Helper function to format minutes to HH:MM format
const formatMinutesToTime = (minutes) => {
  if (!minutes) return '0m';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
};

const TableRow = ({ item, isLast, textColor, expandedId, setExpandedId, timePeriodFilter }) => {
  const isExpanded = expandedId === item.id;
  
  const getTimeDisplay = () => {
    const timeMinutes = (() => {
      switch(timePeriodFilter) {
        case 'week1':
          return item.Week1TimeMinutes || 0;
        case 'week2':
          return item.Week2TimeMinutes || 0;
        case 'week3':
          return item.Week3TimeMinutes || 0;
        case 'week4':
          return item.Week4TimeMinutes || 0;
        case 'twoweeks':
          return item.TwoWeeksTimeMinutes || 0;
        case 'working':
          return item.WorkingTimeMinutes || 0;
        case 'gathering':
          return item.GatheringInfoTimeMinutes || 0;
        default:
          return item.TotalTimeMinutes || 0;
      }
    })();
    return formatMinutesToTime(timeMinutes);
  };

  return (
    <>
      <tr>
        <td
          className={classNames(
            'align-middle font-sans-serif fw-medium text-nowrap',
            {
              'border-bottom-0': isLast && !isExpanded
            }
          )}
          style={{ color: textColor }}
        >
          {item.date}
        </td>
        <td
          className={classNames('align-middle text-center', {
            'border-bottom-0': isLast && !isExpanded
          })}
          style={{ color: textColor }}
        >
          {item.totalSupportCalls}
        </td>
        <td
          className={classNames('align-middle text-center', {
            'border-bottom-0': isLast && !isExpanded
          })}
          style={{ color: textColor }}
        >
          {item.resolved}
        </td>
        <td
          className={classNames('align-middle text-center', {
            'border-bottom-0': isLast && !isExpanded
          })}
          style={{ color: textColor }}
        >
          {item.permanentlyResolved}
        </td>
        <td
          className={classNames('align-middle text-center', {
            'border-bottom-0': isLast && !isExpanded
          })}
          style={{ color: textColor }}
        >
          {item.temporaryResolved}
        </td>
        <td
          className={classNames('align-middle text-center', {
            'border-bottom-0': isLast && !isExpanded
          })}
          style={{ color: textColor }}
        >
          {item.pending}
        </td>
        <td
          className={classNames('align-middle text-center', {
            'border-bottom-0': isLast && !isExpanded
          })}
          style={{ color: textColor }}
        >
          {item.TicketCreationTimeMinutes ? formatMinutesToTime(item.TicketCreationTimeMinutes) : '-'}
        </td>
        <td
          className={classNames('align-middle text-center cursor-pointer', {
            'border-bottom-0': isLast && !isExpanded
          })}
          style={{ color: textColor, backgroundColor: '#e8f4f8' }}
          onClick={() => setExpandedId(isExpanded ? null : item.id)}
          title="Click to see time breakdown"
        >
          <small className="fw-bold">{getTimeDisplay()}</small>
          <br />
          <small className="text-muted">{isExpanded ? '▼' : '▶'}</small>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={7} className="p-3" style={{ backgroundColor: '#f8f9fa' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
              <div>
                <small className="text-muted d-block">Week 1</small>
                <strong>{formatMinutesToTime(item.Week1TimeMinutes)}</strong>
              </div>
              <div>
                <small className="text-muted d-block">Week 2</small>
                <strong>{formatMinutesToTime(item.Week2TimeMinutes)}</strong>
              </div>
              <div>
                <small className="text-muted d-block">Week 3</small>
                <strong>{formatMinutesToTime(item.Week3TimeMinutes)}</strong>
              </div>
              <div>
                <small className="text-muted d-block">Week 4</small>
                <strong>{formatMinutesToTime(item.Week4TimeMinutes)}</strong>
              </div>
              <div>
                <small className="text-muted d-block">2 Weeks</small>
                <strong>{formatMinutesToTime(item.TwoWeeksTimeMinutes)}</strong>
              </div>
              <div>
                <small className="text-muted d-block">Working Time</small>
                <strong>{formatMinutesToTime(item.WorkingTimeMinutes)}</strong>
              </div>
              <div>
                <small className="text-muted d-block">Gathering Info Time</small>
                <strong>{formatMinutesToTime(item.GatheringInfoTimeMinutes)}</strong>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const SupportServicesTable = ({ data, timePeriodFilter = 'total' }) => {
  const [showAll, setShowAll] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const { config } = useContext(AppContext);
  const { isDark } = config;
  const textColor = isDark ? '#fff' : '#212529';
  
  // Filter out rows where all main columns are zero
  const filteredData = data.filter(item => {
    return (
      Number(item.totalSupportCalls) !== 0 ||
      Number(item.resolved) !== 0 ||
      Number(item.permanentlyResolved) !== 0 ||
      Number(item.temporaryResolved) !== 0 ||
      Number(item.pending) !== 0
    );
  });

  const displayData = showAll ? data : filteredData;
  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const lastItemIndex = currentPage * pageSize;
  const firstItemIndex = lastItemIndex - pageSize;
  const currentData = displayData.slice(firstItemIndex, lastItemIndex);

  const handlePaginationClick = page => {
    setCurrentPage(page);
  };

  return (
    <div>
      <SimpleBarReact>
        <Table className="fs--1 mb-0" style={{ color: textColor }}>
          <style>{`
            .support-services-table th, .support-services-table td {
              color: ${textColor} !important;
            }
            .support-services-table thead th {
              background: ${isDark ? '#23272b' : '#f8f9fa'} !important;
            }
            .cursor-pointer {
              cursor: pointer !important;
            }
            .time-spent-column {
              min-width: 120px !important;
              width: 120px !important;
            }
          `}</style>
          <thead className="bg-200 text-800 support-services-table">
            <tr>
              <th colSpan={8} style={{ background: 'inherit', padding: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                  <button
                    className={`btn btn-sm ${showAll ? 'btn-success' : 'btn-outline-secondary'}`}
                    style={{ minWidth: 110, fontWeight: 500, margin: 4 }}
                    onClick={() => setShowAll(v => !v)}
                  >
                    {showAll ? 'Hide 0 Rows' : 'Show All Rows'}
                  </button>
                </div>
              </th>
            </tr>
            <tr>
              <th className="text-nowrap">Initiator</th>
              <th className="text-nowrap text-center">Total Support Tickets</th>
              <th className="text-nowrap text-center">Resolved</th>
              <th className="text-nowrap text-center">Permanently Resolved</th>
              <th className="text-nowrap text-center">Temporary Resolved</th>
              <th className="text-nowrap text-center">Pending</th>
              <th className="text-nowrap text-center">Ticket Creation Time</th>
              <th className="text-nowrap text-center">Time Spent</th>
            </tr>
          </thead>
          <tbody className="support-services-table">
            {currentData.map((item, index) => (
              <TableRow
                key={item.id}
                item={item}
                isLast={displayData.length - 1 === index}
                textColor={textColor}
                expandedId={expandedId}
                setExpandedId={setExpandedId}
                timePeriodFilter={timePeriodFilter}
              />
            ))}
          </tbody>
          <tfoot className="bg-light support-services-table">
            <tr className="text-700 fw-bold">
              <th>Total</th>
              <th className="text-center">{getTotal(displayData, 'totalSupportCalls')}</th>
              <th className="text-center">{getTotal(displayData, 'resolved')}</th>
              <th className="text-center">{getTotal(displayData, 'permanentlyResolved')}</th>
              <th className="pe-x1 text-center">{getTotal(displayData, 'temporaryResolved')}</th>
              <th className="pe-x1 text-center">{getTotal(displayData, 'pending')}</th>
              <th className="pe-x1 text-center">{formatMinutesToTime(getTotal(displayData, 'TicketCreationTimeMinutes'))}</th>
              <th className="pe-x1 text-center">{formatMinutesToTime(getTotal(displayData, 'TotalTimeMinutes'))}</th>
            </tr>
          </tfoot>
        </Table>
      </SimpleBarReact>
      <Pagination className="justify-content-center mt-3">
        {Array.from({ length: Math.ceil(displayData.length / pageSize) }).map((_, index) => (
          <Pagination.Item
            key={index}
            active={index + 1 === currentPage}
            onClick={() => handlePaginationClick(index + 1)}
          >
            {index + 1}
          </Pagination.Item>
        ))}
      </Pagination>
    </div>
  );
};

export default SupportServicesTable;
